import { useFetchLeadStageOptions } from '@repo/config-services';
import { Select, type SelectProps } from 'antd';
import { useCallback, useMemo } from 'react';

export interface LeadStageSelectProps extends Omit<SelectProps, 'options' | 'onFocus'> {
  value?: string;
  onChange: (value: string) => void;
  changeValue: 'id' | 'label';
  withAnyOption?: boolean;
}

export default function LeadStageSelect({
  value,
  onChange,
  changeValue = 'id',
  withAnyOption = false,
  ...props
}: LeadStageSelectProps) {
  const { leadStageOptions, isLoading } = useFetchLeadStageOptions();

  const options = useMemo(
    () => leadStageOptions?.leadAttributeOptions?.map(({ value: val, label }) => ({
      value: val,
      label,
    })) || [],
    [leadStageOptions],
  );

  const finalOptions = useMemo(() => {
    const preparedOptions = [...options];

    if (withAnyOption) {
      preparedOptions.unshift({
        value: 'any',
        label: 'Any',
      });
    }

    return preparedOptions;
  }, [options, withAnyOption]);

  const handleChange = useCallback((changedValue: string) => {
    if (changeValue === 'label') {
      const foundOption = options.find((opt) => opt.value === changedValue);
      if (foundOption) {
        onChange(foundOption.label);
      }
    } else {
      onChange(changedValue);
    }
  }, [onChange, changeValue, options]);

  return (
    <Select
      value={value}
      onChange={handleChange}
      options={finalOptions}
      defaultActiveFirstOption
      loading={isLoading}
      {...props}
    />
  );
}
